import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer } from '@prio365/prio365-react-library';
import classNames from 'classnames';
import { formatDate } from '../incomingInvoiceDateUtils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'react-jss';
import Flex from '../../../../../../components/Flex';
import {
  IncomingInvoiceBaseFields,
  InvoiceKey,
} from '../../../../../../models/IncomingInvoices';
import { PrioTheme } from '../../../../../../theme/types';
import { makePrioStyles } from '../../../../../../theme/utils';
import useCompaniesContext from '../../../../../companies/hooks/useCompaniesContext';
import useOfficesContext from '../../../../../companies/hooks/useOfficesContext';
import useProjectsContext from '../../../../../projects/hooks/useProjectsContext';
import { apiResetAnalyzationResultAsCreator } from '../../../../api';
import { useIncomingInvoiceDrawerContext } from './IncomingInvoiceContextProvider';

const useStyles = makePrioStyles((theme) => ({
  root: {},
  fieldContainer: {
    width: '100%',
    overflowY: 'auto',
  },
  fieldHeader: {
    width: '100%',
  },
  fieldHeading: {
    fontWeight: 'bold',
  },
  field: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing.small,
    width: '100%',
  },
  fieldValue: {
    border: `1px solid ${theme.colors.application.border}`,
    borderRadius: theme.borderRadius.regular,
    padding: theme.spacing.small,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing.extraSmall,
    alignItems: 'center',
    transition:
      'background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease',
    opacity: 0.75,
    fontSize: theme.font.fontSize.small,
  },
  pdfPreview: {
    maxWidth: '1000px',
    overflow: 'auto',
  },
  selected: {
    borderColor: theme.colors.base.green[80],
    backgroundColor: theme.colors.base.green[20],
    opacity: 1,
  },
}));

interface IncomingInvoiceDifferenceDrawerProps {
  className?: string;
  mask: React.ReactNode;
  incomingInvoiceId: string;
}

const ClickableField: React.FC<{
  value: any;
  selected: boolean;
  onClick: () => void;
  className?: string;
}> = ({ value, selected, onClick, className }) => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();

  return (
    <span
      className={classNames(classes.fieldValue, className, {
        [classes.selected]: selected,
      })}
      onClick={onClick}
    >
      <FontAwesomeIcon
        icon={['fal', selected ? 'check-circle' : 'circle']}
        style={{
          color: selected ? theme.colors.base.green.default : undefined,
        }}
      />
      {value || '???'}
    </span>
  );
};

export const INCOMING_INVOICE_FIELD_PAIRS: Partial<
  Record<InvoiceKey, InvoiceKey>
> = {
  fromOfficeId: 'fromOfficeName',
  fromOfficeName: 'fromOfficeId',
};

export const IncomingInvoiceDifferenceDrawer: React.FC<
  IncomingInvoiceDifferenceDrawerProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className, mask, incomingInvoiceId } = props;
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();

  const {
    form,
    differences,
    setDifferences,
    differenceModalVisible,
    setDifferenceModalVisible,
  } = useIncomingInvoiceDrawerContext();

  const { getOfficeById } = useOfficesContext();
  const { getProjectById } = useProjectsContext();
  const { getCompanyById } = useCompaniesContext();

  const getOfficeName = (officeId: string): string | null => {
    const office = getOfficeById ? getOfficeById(officeId) : undefined;
    return office?.name || null;
  };

  const getProjectName = (projectId: string): string | null => {
    const project = getProjectById ? getProjectById(projectId) : undefined;
    return project?.name || null;
  };

  const getCompanyName = (companyId: string): string | null => {
    const company = getCompanyById ? getCompanyById(companyId) : undefined;
    return company?.fullName || null;
  };

  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const setDecisionValue = (diff: any, value: any) => {
    const updatedDiffs = differences.diffs.map((d) =>
      d === diff ? { ...d, decision: { value } } : d
    );
    setDifferences({ ...differences, diffs: updatedDiffs });
  };

  const isNonEmpty = (v: any) => v !== null && v !== undefined && v !== '';

  const saveDecisionValues = async () => {
    if (!differences) return;

    const decisionValues: Partial<IncomingInvoiceBaseFields> = {};

    for (const diff of differences.diffs) {
      const chosen = diff.decision?.value;
      if (!isNonEmpty(chosen)) continue;

      const key = diff.key;
      if (!diff.analyzedFromPair) {
        (decisionValues as any)[key] = chosen;
        continue;
      }

      const pair = INCOMING_INVOICE_FIELD_PAIRS[key];
      const chooseAnalyzed = diff.decision?.value === diff.analyzedValue;

      if (diff.analyzedFromPair && chooseAnalyzed) {
        (decisionValues as any)[pair] = chosen;
        (decisionValues as any)[key] = '';
      } else {
        (decisionValues as any)[key] = chosen;
        (decisionValues as any)[pair] = '';
      }
    }

    form.setFieldsValue({
      ...decisionValues,
    });

    setDifferenceModalVisible(false);
    await apiResetAnalyzationResultAsCreator(incomingInvoiceId);
    form.submit();
  };

  const getFieldValueString = (value: any): string => {
    if (value === null || value === undefined || value === '') return '???';

    if (typeof value === 'string') {
      const dateStr = formatDate(value);
      if (dateStr) return dateStr;

      const officeName = getOfficeName(value);
      if (officeName) return officeName;

      const projectName = getProjectName(value);
      if (projectName) return projectName;

      const companyName = getCompanyName(value);
      if (companyName) return companyName;

      return value;
    }
    if (typeof value === 'number') return value.toString();
    return String(value);
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Drawer
      visible={differenceModalVisible}
      title={t('accounting:incomingInvoices.differenceModal.title')}
      className={classNames(classes.root, className)}
      onClose={() => setDifferenceModalVisible(false)}
      mask={mask}
      maskClosable={false}
      footer={
        <Flex.Row
          justifyContent="space-between"
          childrenGap={theme.spacing.small}
        >
          <Button
            type="default"
            onClick={() => setDifferenceModalVisible(false)}
          >
            {t('accounting:incomingInvoices.drawer.cancel')}
          </Button>
          <Button type="primary" onClick={saveDecisionValues}>
            {t('accounting:incomingInvoices.differenceModal.applySelection')}
          </Button>
        </Flex.Row>
      }
    >
      <Flex.Column
        className={classes.fieldContainer}
        childrenGap={theme.spacing.regular}
      >
        <p>
          {t('accounting:incomingInvoices.differenceModal.description', {
            count: differences?.diffs.length ?? 0,
          })}
        </p>
        <Flex.Row
          className={classes.fieldHeader}
          justifyContent="space-between"
        >
          <span className={classes.fieldHeading}>
            {t('accounting:incomingInvoices.differenceModal.analyzedData')}
          </span>
          <span className={classes.fieldHeading}>
            {t('accounting:incomingInvoices.differenceModal.enteredData')}
          </span>
        </Flex.Row>
        {differences?.diffs.map((diff, index) => (
          <Flex.Column
            key={diff.key ?? index}
            childrenGap={theme.spacing.small}
          >
            <span>
              {t(`accounting:incomingInvoices.fields.${diff.key}.label`)}
            </span>
            <div className={classes.field}>
              <ClickableField
                value={getFieldValueString(diff.analyzedValue)}
                selected={diff.decision?.value === diff.analyzedValue}
                onClick={() => setDecisionValue(diff, diff.analyzedValue)}
              />

              <ClickableField
                value={getFieldValueString(diff.invoiceValue)}
                selected={diff.decision?.value === diff.invoiceValue}
                onClick={() => setDecisionValue(diff, diff.invoiceValue)}
              />
            </div>
          </Flex.Column>
        ))}
      </Flex.Column>
    </Drawer>
  );
};

export default IncomingInvoiceDifferenceDrawer;
